import { useEffect, useRef, type ReactElement, type ReactNode, type Ref } from 'react';
import {
  Button as AriaButton,
  Link as AriaLink,
  type ButtonProps as AriaButtonProps,
  type LinkProps as AriaLinkProps,
} from 'react-aria-components';
import { announcePolite } from './announce';
import { cx } from './cx';
import s from './Button.module.css';

/**
 * gold: celebration CTA · cyan: work CTA (navy text) · navy: primary work button ·
 * ghost: outline on light grounds · ghost-on-dark: outline on the hero and dark bands.
 */
export type ButtonVariant = 'gold' | 'cyan' | 'navy' | 'ghost' | 'ghost-on-dark';
/** All sizes keep a 44 px minimum height; `lg` is the 52 px hero CTA. */
export type ButtonSize = 'sm' | 'md' | 'lg';

const VARIANT: Record<ButtonVariant, string | undefined> = {
  gold: s.gold,
  cyan: s.cyan,
  navy: s.navy,
  ghost: s.ghost,
  'ghost-on-dark': s.ghostOnDark,
};

const SIZE: Record<ButtonSize, string | undefined> = { sm: s.sm, md: undefined, lg: s.lg };

export interface ButtonLook {
  variant?: ButtonVariant | undefined;
  size?: ButtonSize | undefined;
  /** Stretch to the container's width. */
  block?: boolean | undefined;
  className?: string | undefined;
}

export function buttonClassName({
  variant = 'navy',
  size = 'md',
  block = false,
  className,
}: ButtonLook): string {
  return cx(s.btn, VARIANT[variant], SIZE[size], block && s.block, className);
}

interface Content {
  /** Decorative icon before the label. */
  icon?: ReactNode;
  children: ReactNode;
}

export interface ButtonProps
  extends ButtonLook, Content, Omit<AriaButtonProps, 'children' | 'className'> {
  ref?: Ref<HTMLButtonElement>;
}

/**
 * A real `<button>` (React Aria, so press, keyboard and focus behave the same everywhere).
 *
 * `isPending` is handled here rather than by React Aria: the button stays focusable, is marked
 * `aria-disabled`, ignores presses and doesn't submit its form, and "… in progress" is announced as plain text through the app's status line. React Aria's own pending
 * announcement pointed at the button by id and was left behind, unnamed, when the button
 * unmounted (axe: role-img-alt).
 */
export function Button({
  variant,
  size,
  block,
  icon,
  children,
  className,
  isPending,
  onPress,
  type,
  ref,
  ...rest
}: ButtonProps): ReactElement {
  const pending = isPending === true;
  const own = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    if (!pending) return;
    const name = rest['aria-label'] ?? own.current?.textContent.trim() ?? '';
    announcePolite(name === '' ? 'In progress.' : `${name} in progress.`);
    // Only when it becomes pending; the name is read from the button at that moment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending]);
  return (
    <AriaButton
      {...rest}
      ref={(node) => {
        own.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
      }}
      {...(type !== undefined && { type: pending && type === 'submit' ? 'button' : type })}
      {...(!pending && onPress !== undefined && { onPress })}
      {...(pending && { 'aria-disabled': true, 'data-pending': true })}
      className={buttonClassName({ variant, size, block, className })}
    >
      {icon}
      {children}
    </AriaButton>
  );
}

export interface ButtonLinkProps
  extends ButtonLook, Content, Omit<AriaLinkProps, 'children' | 'className' | 'href'> {
  href: string;
  ref?: Ref<HTMLAnchorElement>;
}

/** A real link styled as a button, for actions that go somewhere. */
export function ButtonLink({
  variant,
  size,
  block,
  icon,
  children,
  className,
  ...rest
}: ButtonLinkProps): ReactElement {
  return (
    <AriaLink {...rest} className={buttonClassName({ variant, size, block, className })}>
      {icon}
      {children}
    </AriaLink>
  );
}

export interface InlineLinkButtonProps extends Omit<AriaButtonProps, 'children' | 'className'> {
  children: ReactNode;
}

/** A button that reads like a link inside a sentence (for example "History"). */
export function InlineLinkButton({ children, ...rest }: InlineLinkButtonProps): ReactElement {
  return (
    <AriaButton {...rest} className={s.inlineLink}>
      <span className={s.inlineText}>{children}</span>
    </AriaButton>
  );
}
